import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../core/hooks/use-language';
import { useTranslation } from '../core/hooks/use-translation';
import { useUnreadNotifications } from '../core/hooks/use-unread-notifications';
import { refreshUnreadNotifications } from '../core/notifications/unread-notifications-store';
import { BellIcon } from '../ui/portal-icons';

/** How often the bell asks again, matching WebAdmin's. */
const POLL_INTERVAL_MS = 60_000;

/**
 * The header's bell, for a signed-in customer only: a link to the notifications tab with the unread
 * count on it.
 *
 * **It polls rather than listens**, for the reason WebAdmin's bell does: a minute's delay is not
 * worth a WebSocket. Browser push remains the interruption and this remains the record.
 */
export function NotificationBell() {
    const { t } = useTranslation();
    const language = useLanguage();
    const unread = useUnreadNotifications();

    useEffect(() => {
        const controller = new AbortController();
        void refreshUnreadNotifications(controller.signal);

        const interval = window.setInterval(
            () => void refreshUnreadNotifications(controller.signal),
            POLL_INTERVAL_MS
        );

        return () => {
            window.clearInterval(interval);
            controller.abort();
        };
    }, []);

    const label =
        unread > 0
            ? `${t('portal.notifications')}, ${t('portal.unread')}: ${unread}`
            : t('portal.notifications');

    return (
        <Link
            to={`/${language}/portal/notifications`}
            aria-label={label}
            title={label}
            className="relative inline-flex size-10 shrink-0 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        >
            <BellIcon className="size-5" />
            {unread > 0 && (
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute top-1 right-1 flex h-4 min-w-4 translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] leading-none font-bold text-white tabular-nums"
                >
                    {unread > 9 ? '9+' : unread}
                </span>
            )}
        </Link>
    );
}
